/**
 * Note to the developer:
 *
 * The Motion library doesn't work very well with overflow: auto containers.
 * To work around this, when new cards are added to the player's hand,
 * we temporarily set the hand container to position: absolute and overflow: visible
 * during the layout animation. Once the animation is complete, we revert the styles
 * back to their original state.
 *
 * This allows the layout animation to occur without being clipped by the container's
 * overflow, ensuring a smooth transition when cards are added to the hand.
 *
 * References:
 * - https://stackoverflow.com/questions/79868767/a-framer-motion-animation-on-an-element-entering-a-container-with-overflowauto
 */
import { socket } from "../../socket";
import Card from "../Card";
import CardType from "../../models/card";
import { useEffect, useRef, useState } from "react";
import ChangeSuiteForm from "../ChangeSuitForm";
import MessageType from "../../models/message";
import Modal from "../Modal";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { cardChallengeRejected } from "../../features/game/gameSlice";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import styles from "./Player.module.css";

export default function Player() {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<MessageType | null>(null);
  const container = useRef<HTMLDivElement>(null);
  const hand = useRef<HTMLDivElement>(null);
  const receivingCards = useRef<boolean>(false);

  const roomId = useAppSelector((state) => state.user.roomId);
  const player = useAppSelector((state) => state.user.player);
  const challengeCard = useAppSelector((state) => state.game.challengeCard);
  const game = useAppSelector((state) => state.game.game);

  // 1. Create a ref to store the previous value
  const prevHandRef = useRef<CardType[]>(player.hand);

  // 2. Update the ref value after each render
  useEffect(() => {
    // Check if cards were added to the hand
    if (prevHandRef.current.length < player.hand.length && hand.current) {
      receivingCards.current = true;
    }

    prevHandRef.current = player.hand;
  }, [player.hand]);

  const topCard = game.discardPile[game.discardPile.length - 1];

  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  function handleChangeSuiteFormSubmit(card: CardType, newSuit: string) {
    setIsModalOpen(false);
    socket.emit("play card", roomId, player.id, player.name, card, newSuit);
  }

  function chooseSuit(card: CardType) {
    setMessage({
      title: t("Player.chooseSuit"),
      type: "warning",
      hideCloseButton: true,
      content: (
        <ChangeSuiteForm
          handleSubmit={(newSuit: string) => {
            handleChangeSuiteFormSubmit(card, newSuit);
          }}
        />
      ),
    });
    setIsModalOpen(true);
  }

  function cardPlayed(card: CardType) {
    if (!player.turnToPlay) {
      setMessage({
        title: t("Player.notYourTurnTitle"),
        type: "warning",
        content: t("Player.notYourTurnMessage"),
      });
      setIsModalOpen(true);
      return;
    }

    if (challengeCard) {
      if (card.action !== topCard.action) {
        setMessage({
          title: t("Player.cardNotAllowedTitle"),
          type: "warning",
          content: t("Player.cardNotAllowedMessage"),
        });
        setIsModalOpen(true);
      } else {
        dispatch(cardChallengeRejected());
        if (card.action === "draw-2") {
          socket.emit("play card", roomId, player.id, player.name, card);
        } else if (card.action === "draw-4") {
          chooseSuit(card);
        }
      }
      return;
    }

    if (card.action === "draw-4" || card.action === "wild") {
      chooseSuit(card);
    } else {
      socket.emit("play card", roomId, player.id, player.name, card);
    }
  }

  const activeClass = player.turnToPlay ? styles.active : "";

  return (
    <>
      <div className={`${styles.player} ${activeClass}`} ref={container}>
        <div className={styles.wrapper}>
          {/*
            To correctly animate layout within a scrollable container,
            you must add the layoutScroll prop to the scrollable element.
            This allows Motion to account for the element's scroll offset.
            https://motion.dev/docs/react-layout-animations#animating-within-scrollable-element
          */}
          <motion.div layoutScroll className={styles.hand} ref={hand}>
            {player.hand.map((card) => (
              <motion.div
                layoutId={card.id}
                key={card.id}
                onLayoutAnimationStart={() => {
                  if (receivingCards.current && hand.current) {
                    hand.current.style.left =
                      hand.current.offsetLeft - hand.current.scrollLeft + "px";
                    hand.current.style.top = hand.current.offsetTop + "px";
                    hand.current.style.overflow = "visible";
                    hand.current.style.position = "absolute";
                  }
                }}
                onLayoutAnimationComplete={() => {
                  if (receivingCards.current && hand.current) {
                    hand.current.style.overflow = "auto";
                    hand.current.style.position = "static";

                    // Scroll to the end of the hand where the new cards are
                    hand.current.scrollTo({
                      left: hand.current.scrollWidth,
                      behavior: "smooth",
                    });

                    receivingCards.current = false;
                  }
                }}
              >
                <Card
                  card={card}
                  onClick={() => {
                    cardPlayed(card);
                  }}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {message && (
        <Modal
          open={isModalOpen}
          title={message.title}
          type={message.type}
          handleDismiss={() => {
            setIsModalOpen(false);
          }}
          hideCloseButton={message.hideCloseButton}
        >
          {message.content}
        </Modal>
      )}
    </>
  );
}
